// The admin's dates, in English and in Bosla's own calendar (Cairo), so a
// sign-up just before midnight in Cairo is not shown on the next day by a
// laptop elsewhere. A trading day (YYYY-MM-DD) is a calendar day already,
// so it is read as that day and never moved by a time zone.
//
// Day first, then the short month, as the design writes it: "4 Oct", and
// "Sun 4 Oct" in a list of days. Built from parts rather than one locale's
// whole pattern, because en-GB writes September "Sept" and en-US puts the
// month first.

const BOSLA_TIME_ZONE = 'Africa/Cairo';

function parts(at: Date, timeZone: string, weekday: boolean): string {
	const p = new Intl.DateTimeFormat('en-US', {
		day: 'numeric',
		month: 'short',
		...(weekday ? { weekday: 'short' as const } : {}),
		timeZone,
	}).formatToParts(at);
	const get = (type: string) => p.find((x) => x.type === type)?.value ?? '';
	const dm = `${get('day')} ${get('month')}`;
	return weekday ? `${get('weekday')} ${dm}` : dm;
}

function tradingDay(date: string): Date | null {
	const at = new Date(`${date}T00:00:00Z`);
	return Number.isNaN(at.getTime()) ? null : at;
}

/** A trading day as "4 Oct". */
export function day(date: string): string {
	const at = tradingDay(date);
	return at ? parts(at, 'UTC', false) : date;
}

/** A trading day as "Sun 4 Oct", for a list of days. */
export function dayWithWeekday(date: string): string {
	const at = tradingDay(date);
	return at ? parts(at, 'UTC', true) : date;
}

/** An instant, such as a sign-up, as its day in Cairo: "4 Oct". */
export function instantDay(iso: string): string {
	const at = new Date(iso);
	return Number.isNaN(at.getTime()) ? iso : parts(at, BOSLA_TIME_ZONE, false);
}

// Built once: a visit's list asks it for several times a row.
const CLOCK = new Intl.DateTimeFormat('en-US', {
	hour: '2-digit',
	minute: '2-digit',
	hourCycle: 'h23',
	timeZone: BOSLA_TIME_ZONE,
});

function clock(at: Date): string {
	const p = CLOCK.formatToParts(at);
	const get = (type: string) => p.find((x) => x.type === type)?.value ?? '';
	return `${get('hour')}:${get('minute')}`;
}

/** An instant as "3 Oct 14:20" in Cairo, as an account's history shows it. */
export function instantDayTime(iso: string): string {
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return iso;
	return `${parts(at, BOSLA_TIME_ZONE, false)} ${clock(at)}`;
}

/** An instant as "14:20" in Cairo, as a visit's times show it. */
export function instantTime(iso: string): string {
	const at = new Date(iso);
	return Number.isNaN(at.getTime()) ? iso : clock(at);
}

/** Today in Bosla's calendar, as YYYY-MM-DD. */
export function boslaToday(now: Date = new Date()): string {
	const p = new Intl.DateTimeFormat('en-US', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZone: BOSLA_TIME_ZONE,
	}).formatToParts(now);
	const get = (type: string) => p.find((x) => x.type === type)?.value ?? '';
	return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Whether a typed date is a real day as YYYY-MM-DD: 2026-02-30 is not. */
export function isRealDay(date: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
	const at = tradingDay(date);
	return at !== null && at.toISOString().slice(0, 10) === date;
}

/** Whole days from one calendar day to another: to less from. */
export function daysFrom(from: string, to: string): number {
	const a = tradingDay(from);
	const b = tradingDay(to);
	if (!a || !b) return 0;
	return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** The calendar day n days before a date, as YYYY-MM-DD. */
export function daysBefore(date: string, n: number): string {
	const at = tradingDay(date);
	if (!at) return date;
	return new Date(at.getTime() - n * 86_400_000).toISOString().slice(0, 10);
}
